<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catGreen" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-pill" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="catGreen"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ medications.length }}
      </v-chip>
      <v-tooltip v-if="pendingCount > 0" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            x-small
            color="orange darken-2"
            text-color="white"
            class="ml-1 flex-shrink-0"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon x-small left>mdi-alert-outline</v-icon>
            {{ pendingCount }}
          </v-chip>
        </template>
        <span>{{ pendingCount }} sem doença vinculada</span>
      </v-tooltip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar medicamentos</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-pill-off</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhum medicamento registrado ainda.
        </p>
        <v-btn small color="catGreen" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Adicionar medicamento
        </v-btn>
      </div>

      <!-- Lista de medicamentos -->
      <div v-else class="meds-list">
        <v-chip
          v-for="med in medications"
          :key="med.id"
          class="med-chip"
          :color="med.status === 'using' || !med.status ? '#F0FDF4' : '#F5F5F4'"
          :text-color="med.status === 'using' || !med.status ? '#15803D' : '#78716C'"
          label
        >
          <v-icon left small>mdi-pill</v-icon>
          <span
            class="chip-text"
            :class="{ 'text-stopped': med.status === 'stopped' }"
          >
            {{ med.name }}
          </span>
          <v-chip
            v-if="med.status && med.status !== 'using'"
            x-small
            :color="med.status === 'stopped' ? 'red darken-1' : 'grey darken-1'"
            text-color="white"
            class="ml-2 flex-shrink-0"
          >
            {{ med.status === "stopped" ? "Parou" : "Não sei" }}
          </v-chip>
        </v-chip>
      </div>
    </v-card-text>

    <EditMedicationsDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :dialog-title="`Medicamentos do paciente`"
      :medications="medications"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getMedicationsByPatient } from "@/services/patientMedicationService";
import { getPendencias } from "@/services/correlationService";
import EditMedicationsDialog from "@/features/patients/profile-dashboard/dialogs/EditMedicationsDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "MedicationsCard",

  components: {
    EditMedicationsDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      dialogVisible: false,
      medications: [],
      pendingCount: 0,
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.medications || []).length;
    },
    hasData() {
      return this.medications.length > 0;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.medications = await dashboardStore.section(this.patientId, "medications", () => getMedicationsByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar medicamentos:", error);
        this.medications = [];
      }
      // Pendências: medicamentos sem doença vinculada.
      try {
        const p = await getPendencias(this.patientId);
        this.pendingCount = (p.medications_without_condition || []).length;
      } catch (error) {
        this.pendingCount = 0;
      }
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Verde (Medicamentos) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #bbf7d0 !important;
  box-shadow: 0 2px 10px rgba(21, 128, 61, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #86efac !important;
  box-shadow: 0 6px 20px rgba(21, 128, 61, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: var(--ds-soft-green);
  border-bottom: 1px solid #bbf7d0;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-green);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #86efac;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-cat-green);
}

.meds-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.med-chip {
  font-size: 12px !important;
  font-weight: 600 !important;
  height: auto !important;
  min-height: 38px !important;
  padding: 8px 12px !important;
  white-space: normal !important;
  overflow-wrap: break-word;
  word-break: normal;
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 10px !important;
}

.chip-text {
  flex: 1;
  text-align: left;
  line-height: 1.4;
}

.text-stopped {
  text-decoration: line-through;
  opacity: 0.75;
}
</style>
